"use client";

import { useContext } from "react";
import { FormFunnelContext } from "./FormFunnelContext";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import { DreamLogo } from "@portal/app/_components/ui-elements/DreamLogo";
import { boundProgress } from "./utils/funnelProgress";

export interface FormFunnelHeaderProps {
  currentStepIndex?: number;
  totalSteps?: number;
  isComplete?: boolean;
  /** Overall journey progress supplied by the page, independent of hosted step counts. */
  progressPercent?: number;
  topOffset?: number;
  /** Disable the customer navigation link in inert CMS previews. */
  logoClickable?: boolean;
}

export function FormFunnelHeader({
  currentStepIndex: propCurrentStepIndex,
  totalSteps: propTotalSteps,
  isComplete: propIsComplete,
  progressPercent,
  topOffset = 0,
  logoClickable = true,
}: FormFunnelHeaderProps = {}) {
  const context = useContext(FormFunnelContext);

  // Determine values from props or context
  const currentStepIndex = propCurrentStepIndex ?? context?.currentStepIndex ?? 0;
  const totalSteps = propTotalSteps ?? context?.totalSteps ?? 0;
  const isComplete = propIsComplete ?? context?.isComplete ?? false;

  const progress = boundProgress(
    progressPercent ?? (isComplete ? 100 : ((currentStepIndex + 1) / (totalSteps + 1)) * 100)
  );

  return (
    <HeaderContainer data-testid="form-funnel-header" sx={{ top: topOffset }}>
      <HeaderContent maxWidth="sm">
        <HeaderLogo height={24} clickable={logoClickable} />
        <ProgressBarWrapper data-testid="funnel-progress-bar">
          <StyledLinearProgress variant="determinate" value={progress} />
        </ProgressBarWrapper>
      </HeaderContent>
    </HeaderContainer>
  );
}

const HeaderContainer = styled(Box)`
  position: sticky;
  z-index: ${({ theme }) => theme.zIndex.appBar};
  width: 100%;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.dream.borderColor};
  background-color: ${({ theme }) => theme.palette.common.white};
`;

const HeaderContent = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const HeaderLogo = styled(DreamLogo)`
  img {
    display: block;
    width: auto;
    height: 24px;
    filter: brightness(0);
  }
`;

const ProgressBarWrapper = styled(Box)`
  width: 100%;
`;

const StyledLinearProgress = styled(LinearProgress)`
  height: 6px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.palette.dream.borderColor};
  overflow: hidden;

  .MuiLinearProgress-bar {
    border-radius: 999px;
    background-color: ${({ theme }) => theme.palette.primary.main};
  }
`;
